import type { Account, Site, User } from "./model.ts";

export interface ChecklistDevice {
  devEui: string;
  name: string;
  /** Last time anything was heard from it */
  lastSeenAt?: number;
  /** Ports switched on in set-up */
  enabledPorts: number;
}

export interface ChecklistItem {
  id: string;
  label: string;
  ok: boolean;
  /** Plain-English reason when it fails */
  detail?: string;
  /** Failing a required item blocks go-live unless an FMS admin overrides with a reason. */
  required: boolean;
}

const HOUR = 3_600_000;

/**
 * What FMS staff check before handing an account to the customer. Shown as a list in
 * Admin; "Go live" is enabled when every required item passes.
 */
export function goLiveChecklist(
  account: Account,
  sites: readonly Site[],
  users: readonly User[],
  devices: readonly ChecklistDevice[],
  now: number,
): ChecklistItem[] {
  const customers = users.filter((u) => u.accountId === account.id && u.status !== "disabled");
  const owners = customers.filter((u) => u.role === "owner");
  const silent = devices.filter((d) => !d.lastSeenAt || now - d.lastSeenAt > HOUR);
  const unconfigured = devices.filter((d) => d.enabledPorts === 0);
  const badEmails = customers.filter((u) => emailProblem(u.email) !== null);

  return [
    {
      id: "site",
      label: "At least one site set up",
      ok: sites.length > 0,
      ...(sites.length ? {} : { detail: "Add the farm as a site." }),
      required: true,
    },
    {
      id: "owner",
      label: "An owner is added",
      ok: owners.length > 0,
      ...(owners.length ? {} : { detail: "Every account needs one owner for billing and users." }),
      required: true,
    },
    {
      id: "emails",
      label: "Every user has a valid email",
      ok: badEmails.length === 0,
      ...(badEmails.length ? { detail: `Check: ${badEmails.map((u) => u.email || u.name).join(", ")}` } : {}),
      required: true,
    },
    {
      id: "devices",
      label: "At least one device added",
      ok: devices.length > 0,
      ...(devices.length ? {} : { detail: "Add the devices for this farm." }),
      required: true,
    },
    {
      id: "online",
      label: "Every device heard from in the last hour",
      ok: devices.length > 0 && silent.length === 0,
      ...(silent.length ? { detail: `Not heard from: ${silent.map((d) => d.name).join(", ")}` } : {}),
      required: true,
    },
    {
      id: "ports",
      label: "Ports set up on every device",
      ok: devices.length > 0 && unconfigured.length === 0,
      ...(unconfigured.length ? { detail: `No ports enabled: ${unconfigured.map((d) => d.name).join(", ")}` } : {}),
      required: false,
    },
  ];
}

export function readyToGoLive(items: readonly ChecklistItem[]): boolean {
  return items.every((i) => i.ok || !i.required);
}

/** Misspellings of the mail services farmers use most: these domains don't take mail. */
const DOMAIN_TYPOS: Record<string, string> = {
  "gmnail.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.con": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "outlok.com": "outlook.com",
  "bigpond.con": "bigpond.com",
  "bigpong.com": "bigpond.com",
};

/**
 * Why an email address won't work (to say before an invite goes to it), or null. Catches
 * what's clearly wrong ("jo@rivermount", "x@gmnail.com", "…ccom.au"), not every bad address.
 */
export function emailProblem(email: string): string | null {
  const e = email.trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.([a-z]{2,}|xn--[a-z0-9-]+)$/.test(e)) return "That isn't a whole email address (like name@farm.com.au).";
  const domain = e.split("@")[1]!;
  if (DOMAIN_TYPOS[domain]) return `Check the email: did you mean ${DOMAIN_TYPOS[domain]}?`;
  if (/\.(con|ccom|comm|ocm)(\.|$)|\.\./.test(domain)) return "Check the email: the part after @ looks mistyped.";
  return null;
}
